import { EllipsisOutlined, EyeOutlined, UserOutlined } from '@ant-design/icons'
import { Avatar, Breadcrumb, Button, Dropdown, Typography } from 'antd'
import { type JSX } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

import { BORDER, MUTED_TEXT, SURFACE, TEXT } from '../colors'

import { SMS_PROVIDER_LABELS } from './General/constants'
import { getMemberCountChannels, useMemberCount } from './General/queries'
import { useImpersonate } from './hooks/useImpersonate'
import { ACCOUNT_STATUS_DISPLAY, pluralize } from './Overview/health'
import { StatusBadge } from './StatusBadge'

import { Client } from 'api/clients'
import { ROUTES } from 'constants/routes'
import { formatPhone } from 'utils/formatters'
import { formatName } from 'utils/name'
import { route } from 'utils/router'

const StyledHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 32px 20px;
  background: ${SURFACE};
  border-bottom: 1px solid ${BORDER};
`

const StyledTitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const StyledIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`

const StyledName = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;

  h1 {
    margin: 0;
    color: ${TEXT};
    font-size: 24px;
    font-weight: 700;
    line-height: 1.25;
  }
`

const StyledMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  color: ${MUTED_TEXT};
  font-size: 13px;

  .ant-typography {
    color: inherit;
    font-size: inherit;
  }
`

const StyledId = styled.span`
  white-space: nowrap;
`

const StyledMetaItem = styled.span`
  &::before {
    content: '·';
    margin-right: 8px;
  }
`

const StyledActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MOBILE_IMPERSONATE_KEY = 'impersonate-mobile'

type ClientHeaderProps = {
  client: Client
  sectionLabel?: string
  accountStatus?: string
}

export function ClientHeader({ client, sectionLabel, accountStatus }: ClientHeaderProps): JSX.Element {
  const impersonate = useImpersonate(client.id)
  const memberCount = useMemberCount({
    clientId: client.id,
    communicationChannels: getMemberCountChannels(client.communicationChannels),
  })
  const name = formatName(client)
  const statusBadge = accountStatus ? ACCOUNT_STATUS_DISPLAY[accountStatus] : undefined

  return (
    <StyledHeader>
      <Breadcrumb
        items={[
          { title: <Link to={ROUTES.CLIENTS}>Clients</Link> },
          { title: <Link to={route(ROUTES.CLIENT.OVERVIEW, { id: client.id })}>{name}</Link> },
          ...(sectionLabel ? [{ title: sectionLabel }] : []),
        ]}
      />
      <StyledTitleRow>
        <StyledIdentity>
          <Avatar alt={name} icon={<UserOutlined />} size={44} src={client.profileImageSmall?.url} />
          <div>
            <StyledName>
              <h1>{name}</h1>
              {statusBadge && (
                <StatusBadge tone={statusBadge.tone} withDot>
                  {statusBadge.label}
                </StatusBadge>
              )}
            </StyledName>
            <StyledMeta>
              <StyledId>
                ID{' '}
                <Typography.Text code copyable={{ text: client.id, tooltips: ['Copy client ID', 'Copied'] }}>
                  {client.id}
                </Typography.Text>
              </StyledId>
              {!memberCount.isLoading && (
                <StyledMetaItem>
                  {memberCount.isError ? 'Could not load the member count' : pluralize(memberCount.total, 'member')}
                </StyledMetaItem>
              )}
              {client.masterPhoneNumber && (
                <StyledMetaItem>
                  <Typography.Text
                    copyable={{
                      text: client.masterPhoneNumber.replace(/\D/g, ''),
                      tooltips: ['Copy phone number', 'Copied'],
                    }}
                  >
                    {formatPhone(client.masterPhoneNumber)}
                  </Typography.Text>
                </StyledMetaItem>
              )}
              <StyledMetaItem>
                Connectivity Partner: {SMS_PROVIDER_LABELS[client.smsProvider] || client.smsProvider}
              </StyledMetaItem>
            </StyledMeta>
          </div>
        </StyledIdentity>
        <StyledActions>
          <Button icon={<EyeOutlined />} onClick={() => impersonate()} size="large" type="primary">
            Impersonate
          </Button>
          <Dropdown
            menu={{
              items: [{ key: MOBILE_IMPERSONATE_KEY, label: 'Impersonate in mobile app' }],
              onClick: ({ key }) => {
                if (key === MOBILE_IMPERSONATE_KEY) impersonate({ mobile: true })
              },
            }}
            trigger={['click']}
          >
            <Button aria-label="More client actions" icon={<EllipsisOutlined />} size="large" />
          </Dropdown>
        </StyledActions>
      </StyledTitleRow>
    </StyledHeader>
  )
}
